import React, { useEffect, useState } from "react"
import { useNavigate, useParams } from "react-router"
import { useSelector } from 'react-redux'
import { useTranslation } from 'react-i18next'
import { Modal, Dropdown } from 'react-bootstrap'
import Select from 'react-select'

import FilterPagination from 'pages/parts/FilterPagination'
import PeriodPicker from 'pages/parts/PeriodPicker'

import { httpOk, GET, POST } from 'helpers/api'
import { formatDate, formatMoney, returnSign, PAGE_SIZES } from 'helpers/helpers'
import { businessToday, businessMonthStart } from 'helpers/businessDay'

function Transactions() {
	const { t } = useTranslation()
	const navigate = useNavigate()
	const { id } = useParams()
	const reduxAccount = useSelector(state => state.account)

	const transactionType = [
		{ 'id': 'plus', 'name': t('debit') },
		{ 'id': 'minus', 'name': t('credit') },
	]


	const [data, setData] = useState({})
	const [accounts, setAccounts] = useState([])
	const [dropdownIsOpen, setDropdownIsOpen] = useState(false)
	const [showTransferModal, setShowTransferModal] = useState(false)
	const [transfer, setTransfer] = useState({})
	const [filterData, setFilterData] = useState({
		start_date: businessMonthStart(),
		end_date: businessToday(),
		pos_id: reduxAccount.pos_id,
		transaction_type: null,
		size: 20,
	})
	const [pagination, setPagination] = useState({
		url: `/finance/accounts/${id}/transactions`,
		size: 20,
		response: null
	})

	function buildAccountOptions(list = []) {
		const childrenOf = {}
		const known = new Set(list.map((item) => Number(item.id)))
		list.forEach((item) => {
			const parentId = known.has(Number(item.parent_id)) ? Number(item.parent_id) : null
			if (!childrenOf[parentId]) childrenOf[parentId] = []
			childrenOf[parentId].push(item)
		})
		const options = []
		const walk = (parentId, level) => {
			(childrenOf[parentId] || []).forEach((item) => {
				const hasChildren = (childrenOf[Number(item.id)] || []).length > 0
				options.push({
					...item,
					isDisabled: hasChildren,
					treeLabel: `${'\u00A0\u00A0\u00A0\u00A0'.repeat(level)}${item.name}${item.code ? ` (${item.code})` : ''}`,
				})
				walk(Number(item.id), level + 1)
			})
		}
		walk(null, 0)
		return options
	}

	async function searchWithFilter(passedData = {}) {
		var filterUrl = `/finance/accounts/${id}/transactions`
		var urlParams = ""
		const filterDataCopy = { ...filterData, ...(passedData.filterData || {}) }

		if (filterDataCopy.pos_id)
			urlParams += returnSign(urlParams) + 'pos_id=' + filterDataCopy.pos_id
		if (filterDataCopy.transaction_type)
			urlParams += returnSign(urlParams) + 'transaction_type=' + filterDataCopy.transaction_type
		if (filterDataCopy.start_date)
			urlParams += returnSign(urlParams) + 'start_date=' + filterDataCopy.start_date
		if (filterDataCopy.end_date)
			urlParams += returnSign(urlParams) + 'end_date=' + filterDataCopy.end_date
		if (filterDataCopy.size)
			urlParams += returnSign(urlParams) + 'size=' + filterDataCopy.size
		if (filterDataCopy.page)
			urlParams += returnSign(urlParams) + 'page=' + filterDataCopy.page

		filterUrl = filterUrl + urlParams

		const response = await GET(filterUrl, {}, { loader: true })
		if (httpOk(response)) {
			setData(response.data)
			setPagination({ ...pagination, 'url': filterUrl, 'size': filterDataCopy.size, 'response': response.data })
		}

		setFilterData(filterDataCopy)
		setDropdownIsOpen(false)
	}

	async function getAccounts() {
		const response = await GET('/helper/accounts')
		if (httpOk(response)) setAccounts(response.data)
	}

	function openTransferModal() {
		setTransfer({
			account_from_id: Number(id),
			account_to_id: null,
			amount: '',
			note: '',
		})
		setShowTransferModal(true)
	}

	async function submitTransfer(e) {
		e.preventDefault()
		if (!transfer.account_from_id || !transfer.account_to_id || !transfer.amount) return

		const response = await POST('/finance/accounts/transfer', {
			pos_id: filterData.pos_id,
			account_from_id: transfer.account_from_id,
			account_to_id: transfer.account_to_id,
			amount: transfer.amount,
			note: transfer.note || null,
		}, { loader: true })

		if (httpOk(response)) {
			setShowTransferModal(false)
			searchWithFilter()
		}
	}

	async function paginate(page) {
		const nextFilterData = { ...filterData, 'page': page }
		searchWithFilter({ filterData: nextFilterData })
	}

	function handlePageSizeChange(size) {
		const nextFilterData = { ...filterData, size, page: null }
		searchWithFilter({ filterData: nextFilterData })
	}

	useEffect(() => {
		getAccounts()
		searchWithFilter()
	}, []) // eslint-disable-line react-hooks/exhaustive-deps

	const accountOptions = buildAccountOptions(accounts)
	const accountSelectProps = {
		options: accountOptions,
		menuPosition: 'fixed',
		menuPortalTarget: typeof document !== 'undefined' ? document.body : null,
		menuPlacement: 'auto',
		styles: { menuPortal: (base) => ({ ...base, zIndex: 9999 }) },
		placeholder: '',
		noOptionsMessage: () => t('list_empty'),
		getOptionLabel: (option) => option.treeLabel || option.name,
		getOptionValue: (option) => option.id,
		isOptionDisabled: (option) => !!option.isDisabled,
	}

	return (
		<>
			<div className="page-header d-flex text-truncate">
				<div className="vertical-center cursor" onClick={() => navigate('/finance/accounts')} title={t('back')}>
					<i className="uil uil-angle-left fz-24"></i>
				</div>
				<h4 className="vertical-center h-100">
					{t('account')}
				</h4>
				<div className="vertical-center">
					<i className="uil uil-angle-double-right fz-20"></i>
				</div>
				<div className="vertical-center">
					<h6 className="mb-0">
						{data?.account?.name}
						{data?.account?.code ? ` (${data.account.code})` : ''}
					</h6>
				</div>
			</div>

			<div className="card">
				<div className="card-body">
					<div className="d-flex justify-content-between flex-wrap mb-3 gap-2">
						<div className="d-flex gap-2">
							<Dropdown onToggle={() => setDropdownIsOpen(!dropdownIsOpen)} show={dropdownIsOpen}>
								<Dropdown.Toggle variant="outline-primary" className="btn-wide h-100">
									{t('filter')}
								</Dropdown.Toggle>
								<Dropdown.Menu className="dropdown-filter">
									<div className="d-flex gap-2">
										<div className="form-group w-100">
											<label>{t('transaction_type')}</label>
											<Select
												isClearable
												options={transactionType}
												value={transactionType.find(option => option.id === filterData.transaction_type) || ''}
												onChange={(option) => setFilterData({ ...filterData, 'transaction_type': option ? option.id : null })}
												placeholder=""
												noOptionsMessage={() => t('list_empty')}
												getOptionLabel={(option) => option.name}
												getOptionValue={(option) => option.id}
											/>
										</div>
									</div>
									<div className="form-group">
										<label>{t('period')}</label>
										<PeriodPicker
											withTime={false}
											startDate={filterData.start_date}
											endDate={filterData.end_date}
											onChange={(period) => setFilterData({ ...filterData, ...period })}
										/>
									</div>
									<div className="row mt-3">
										<div className="col-md-12">
											<div className="d-flex justify-content-end gap-2">
												<button className="btn btn-secondary" onClick={() => setDropdownIsOpen(false)}>
													{t('cancel')}
												</button>
												<button className="btn btn-primary" onClick={() => searchWithFilter({ filterData: { ...filterData } })}>
													{t('apply')}
												</button>
											</div>
										</div>
									</div>
								</Dropdown.Menu>
							</Dropdown>

							<button className="btn btn-primary btn-wide" onClick={() => openTransferModal()}>
								{t('transfer')}
							</button>
						</div>

						<div className="d-flex gap-3">
							<div className="vertical-center">
								<span className="me-1">{t('debit')}:</span>
								<b className="text-success">{formatMoney(data.total_debit)}</b>
							</div>
							<div className="vertical-center">
								<span className="me-1">{t('credit')}:</span>
								<b className="text-danger">{formatMoney(data.total_credit)}</b>
							</div>
							{data?.account?.balance != null &&
								<div className="vertical-center">
									<span className="me-1">{t('balance')}:</span>
									<b className={Number(data.account.balance) < 0 ? 'text-danger' : ''}>
										{formatMoney(data.account.balance)}
									</b>
								</div>
							}
						</div>
					</div>

					<div className="table-responsive">
						<table className="table table-bordered table-hover mb-0">
							<thead>
								<tr>
									<th style={{ 'minWidth': '33px' }} className="text-center">#</th>
									<th style={{ 'width': '12%' }} className="text-center">{t('date')}</th>
									<th style={{ 'width': '16%' }}>{t('from_account')}</th>
									<th style={{ 'width': '16%' }}>{t('to_account')}</th>
									<th style={{ 'width': '18%' }}>{t('note')}</th>
									<th style={{ 'width': '10%' }}>{t('who')}</th>
									<th style={{ 'width': '9%' }} className="text-end">{t('debit')}</th>
									<th style={{ 'width': '9%' }} className="text-end">{t('credit')}</th>
									<th style={{ 'width': '10%' }} className="text-end">{t('balance')}</th>
								</tr>
							</thead>
							<tbody>
								{data?.data?.length > 0 ? data.data.map((item, index) => (
									<tr key={index} className={item.status === -1 ? 'text-decoration-line-through text-muted' : ''}>
										<td className="text-center">{item.row_number}</td>
										<td className="text-center text-nowrap">{formatDate(item.created_at, 'dd.MM.yy HH:mm')}</td>
										<td className="td-ellipsis" title={item.from_account_name}>
											{item.from_account_name || '—'}
											{item.from_account_code ? ` (${item.from_account_code})` : ''}
										</td>
										<td className="td-ellipsis" title={item.to_account_name}>
											{item.to_account_name || '—'}
											{item.to_account_code ? ` (${item.to_account_code})` : ''}
										</td>
										<td className="td-ellipsis" title={item.note}>
											{item.note || item.payment_purpose_name || (item.entity_type === 'account_transfer' ? t('transfer') : '')}
										</td>
										<td className="td-ellipsis">{item.created_by_name || item.created_by}</td>
										<td className="text-end text-nowrap">
											{item.transaction_type === 'plus' && <span className="text-success">{formatMoney(item.amount)}</span>}
										</td>
										<td className="text-end text-nowrap">
											{item.transaction_type === 'minus' && <span className="text-danger">{formatMoney(item.amount)}</span>}
										</td>
										<td className={`text-end text-nowrap ${item.running_balance >= 0 ? '' : 'text-danger'}`}>
											{formatMoney(item.running_balance)}
										</td>
									</tr>
								)) : (
									<tr>
										<td colSpan="9" className="text-center text-muted py-4">{t('list_empty')}</td>
									</tr>
								)}
							</tbody>
						</table>
					</div>

					{pagination.response &&
						<FilterPagination
							pagination={pagination}
							paginate={paginate}
							showPageSizeSelect
							pageSizeOptions={PAGE_SIZES}
							onPageSizeChange={handlePageSizeChange}>
						</FilterPagination>
					}
				</div>
			</div>

			{/* TRANSFER MODAL */}
			<Modal show={showTransferModal} animation={false} centered
				dialogClassName="update-modal-width"
				onHide={() => setShowTransferModal(false)}>
				<Modal.Header closeButton>
					<Modal.Title>{t('transfer')}</Modal.Title>
				</Modal.Header>
				<Modal.Body>
					<form onSubmit={submitTransfer} autoComplete="off">
						<div className="form-group">
							<label>Со счёта</label>
							<Select
								{...accountSelectProps}
								value={accountOptions.find(option => option.id === transfer.account_from_id) || ''}
								onChange={(option) => setTransfer({ ...transfer, 'account_from_id': option ? option.id : null })}
							/>
						</div>
						<div className="form-group">
							<label>На счёт</label>
							<Select
								{...accountSelectProps}
								value={accountOptions.find(option => option.id === transfer.account_to_id) || ''}
								onChange={(option) => setTransfer({ ...transfer, 'account_to_id': option ? option.id : null })}
							/>
						</div>
						<div className="form-group">
							<label>{t('amount')}<span className="required-mark">*</span></label>
							<input type="text" className="form-control" name="amount" autoFocus
								value={transfer.amount ? formatMoney(transfer.amount) : ''}
								onChange={(e) => setTransfer({ ...transfer, 'amount': e.target.value.replace(/[^0-9.]/g, '') })} />
						</div>
						<div className="form-group">
							<label>{t('note')}</label>
							<input type="text" className="form-control" name="note"
								value={transfer.note || ''}
								onChange={(e) => setTransfer({ ...transfer, 'note': e.target.value })} />
						</div>
						<div className="d-flex w-100 mt-3 gap-2">
							<button type="button" className="btn btn-outline-warning w-100"
								onClick={() => setShowTransferModal(false)} tabIndex="-1">
								{t('cancel')}
							</button>
							<button type="submit" className="btn btn-primary w-100"
								disabled={!transfer.account_from_id || !transfer.account_to_id || !transfer.amount || transfer.account_from_id === transfer.account_to_id}>
								{t('save')}
							</button>
						</div>
					</form>
				</Modal.Body>
			</Modal>
			{/* TRANSFER MODAL */}
		</>
	)
}

export default Transactions
